import React, { useState } from 'react';
import { Box, Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import {
  CheckCircle as GoingIcon,
  Star as InterestedIcon,
  StarBorder as InterestedOutlineIcon,
  Cancel as DeclinedIcon,
  ArrowDropDown as ArrowIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import { RSVP_LABELS } from './eventMeta';

const ICONS = { going: GoingIcon, interested: InterestedIcon, declined: DeclinedIcon };

/** Facebook-style answer: "Interested" and "Going" until you answer, then one button with your answer and
 *  a menu to change it. onChange gets the event as the server returns it (fresh counts). MENU_Z lifts the
 *  menu above full-screen overlays such as the ProposalWriter. */
export default function RsvpButton({ event, onChange, size = 'small', fullWidth = false, menuZ }) {
  const [anchor, setAnchor] = useState(null);
  const [busy, setBusy] = useState(false);
  const { showSnackbar } = useSnackbar() || {};
  const answer = event.my_rsvp;
  const closed = event.status === 'cancelled' || event.status === 'past';

  const send = async (status) => {
    setAnchor(null);
    setBusy(true);
    try {
      const url = `/events/${event.id}/rsvp/`;
      const { data } = status ? await axiosService.post(url, { status }) : await axiosService.delete(url);
      onChange?.(data);
    } catch (err) {
      showSnackbar?.(apiErrorMessage(err, 'Could not save your answer.'), 'error');
    } finally {
      setBusy(false);
    }
  };
  const stop = (e) => e.stopPropagation();

  if (closed && !answer) return null;

  if (!answer || answer === 'invited') {
    return (
      <Box sx={{ display: 'flex', gap: 1, width: fullWidth ? '100%' : 'auto' }} onClick={stop}>
        <Button size={size} variant="outlined" disabled={busy || closed} startIcon={<InterestedOutlineIcon />}
          onClick={() => send('interested')} sx={{ textTransform: 'none', flex: fullWidth ? 1 : 'none' }}>
          Interested
        </Button>
        <Button size={size} variant="contained" disableElevation disabled={busy || closed} startIcon={<GoingIcon />}
          onClick={() => send('going')} sx={{ textTransform: 'none', flex: fullWidth ? 1 : 'none' }}>
          Going
        </Button>
      </Box>
    );
  }

  const Icon = ICONS[answer] || GoingIcon;
  return (
    <Box onClick={stop} sx={{ width: fullWidth ? '100%' : 'auto' }}>
      <Button size={size} variant={answer === 'declined' ? 'outlined' : 'contained'} disableElevation
        color={answer === 'declined' ? 'inherit' : 'primary'} disabled={busy || closed} fullWidth={fullWidth}
        startIcon={<Icon />} endIcon={<ArrowIcon />} onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ textTransform: 'none' }}>
        {RSVP_LABELS[answer]}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        sx={menuZ ? { zIndex: menuZ } : undefined}>
        {['going', 'interested', 'declined'].map((s) => {
          const ItemIcon = ICONS[s];
          return (
            <MenuItem key={s} selected={s === answer} onClick={() => send(s)}>
              <ListItemIcon><ItemIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{RSVP_LABELS[s]}</ListItemText>
            </MenuItem>
          );
        })}
        <MenuItem onClick={() => send(null)}>
          <ListItemIcon><UndoIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Remove my answer</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
